// project imports
import {
  ApplicableAction,
  EAIEntityTypeCode,
  InputType
} from 'utils/constants';

// types
import { filterOptionsProps, InputOption, RadioInputOption } from 'models';
import {
  CaCodeType,
  ContextAction,
  GetContextActionsRequestBody,
  ResourceContextAction
} from 'models/contextActions';

// ==============================|| CONTEXT ACTIONS UTILS ||============================== //

export const createContextActionPayload = (
  contextActions: ContextAction[] = [],
  resourceEntity = 'USER',
  identificator?: string | undefined
): ResourceContextAction[] => [
  { contextActions, resourceEntity, identificator }
];

export const mergeContextActions = (
  existingStateContextActions: ResourceContextAction[],
  sourceActionContextActions: ResourceContextAction[]
): ResourceContextAction[] => {
  const resultMap = new Map<string, ResourceContextAction>();

  const mergeActions = (
    target: ResourceContextAction,
    source: ResourceContextAction
  ): void => {
    const allContextActions: ContextAction[] = [
      ...target.contextActions,
      ...source.contextActions
    ];

    const uniqueValuesMap: Record<string, ContextAction> = {};

    // Iterate through the array in reverse order
    for (let i = allContextActions.length - 1; i >= 0; i--) {
      const item = allContextActions[i];
      const key = `${item.code}`;

      // If the key is not in the map, add it with the current item
      if (!uniqueValuesMap[key]) {
        uniqueValuesMap[key] = item;
      }
    }

    target.contextActions = [];
    target.contextActions.push(...Object.values(uniqueValuesMap));
  };

  const addToResultMap = (item: ResourceContextAction): void => {
    const key = `${item.resourceEntity}-${item.identificator}`;
    const existingItem = resultMap.get(key);

    if (existingItem) {
      mergeActions(existingItem, item);
    } else {
      resultMap.set(key, { ...item });
    }
  };

  // has to be in this order
  // as source can replace already existing context action
  // and unique values are created in reversed order - LIFO
  existingStateContextActions.forEach(addToResultMap);
  sourceActionContextActions.forEach(addToResultMap);

  return Array.from(resultMap.values());
};

const shouldFilterOptions = (contextAction?: ContextAction) =>
  !contextAction ||
  [ApplicableAction.NOT_AVAILABLE, ApplicableAction.FORBIDDEN].includes(
    contextAction.applicability
  );

type FilterOption = { value: string | bigint; helperText?: string };

export const filterOptionsByCA = ({
  module,
  inputType,
  options,
  contextAction,
  value
}: filterOptionsProps<InputOption | RadioInputOption>):
  | InputOption[]
  | RadioInputOption[] => {
  const filterOptions = <T extends FilterOption>(collection: T[]): T[] =>
    collection.filter((option) => option.value !== value);

  const mapOptions = <T extends FilterOption>(collection: T[]): T[] => {
    return collection.map((option) => {
      if (option.value !== value || !contextAction) {
        return option;
      }
      const helperText = `module.${module}.contextActions.${contextAction.reasonCode}`;
      return { ...option, helperText };
    });
  };

  if (shouldFilterOptions(contextAction)) {
    return inputType === InputType.RADIO_GROUP
      ? filterOptions(options as RadioInputOption[])
      : filterOptions(options as InputOption[]);
  }

  if (contextAction?.applicability === ApplicableAction.DISABLED) {
    return inputType === InputType.RADIO_GROUP
      ? mapOptions(options as RadioInputOption[])
      : mapOptions(options as InputOption[]);
  }

  return inputType === InputType.RADIO_GROUP
    ? (options as RadioInputOption[])
    : (options as InputOption[]);
};

export const getCaRequestBody = (
  contextActions: CaCodeType[],
  identificators: string[]
): GetContextActionsRequestBody => [
  {
    resourceEntity: EAIEntityTypeCode.USER,
    identificators: identificators,
    contextActions
  }
];
